import { useLanguage, useT } from "../../contexts/LanguageContext";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PaperAirplaneIcon,
  XMarkIcon,
  ArrowUturnLeftIcon,
  TrashIcon,
  PencilSquareIcon,
  ArrowRightCircleIcon,
  UserCircleIcon,
  PaperClipIcon,
  MicrophoneIcon,
  StopIcon,
  PhoneIcon,
  PhoneXMarkIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/outline";
import {
  getMessages,
  closeChat,
  getUserProfile,
  uploadChatFile,
  uploadChatAudio,
} from "../../api/operator";
import { initializeSocket } from "../../utils/socketUtils";
import useVoiceCall from "../../hooks/useVoiceCall";
import { parseVoiceCall, renderBold, voiceCallLabel } from "./messageFormat";

// Жүйелік желінің id-і бұрын осы жерде де қатып тұратын. Енді ол бекендтен
// (`GET /operator/chats/{id}/messages` → `actor_user_id`) келеді.

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
};

const KK_MONTHS = ["қаңтар", "ақпан", "наурыз", "сәуір", "мамыр", "маусым", "шілде", "тамыз", "қыркүйек", "қазан", "қараша", "желтоқсан"];

const dayKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

/** Күн бөлгіші: «Бүгін» / «Кеше» / «12 қыркүйек» (басқа жыл болса — жылымен). */
const formatDay = (iso, t, lang) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (dayKey(d) === dayKey(now)) return t("chat.today");
  if (dayKey(d) === dayKey(yesterday)) return t("chat.yesterday");
  const year = d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : "";
  if (lang === "ru") return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" }) + year;
  return `${d.getDate()} ${KK_MONTHS[d.getMonth()]}${year}`;
};

/** Сілтеме суретке ұқсай ма. Мобильді клиенттер суретті `message_type: "file"`
 *  (кейде тіпті "text") етіп жібереді де, `file_name` бос болады — сондықтан
 *  түрі бойынша емес, URL кеңейтімі бойынша танимыз. */
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|heic|heif)(\?|#|$)/i;
const AUDIO_EXT = /\.(webm|m4a|mp3|ogg|oga|wav|aac)(\?|#|$)/i;
// Бөлуге арналған нұсқада `g` керек, ал тексеруге БӨЛЕК (жалаушасыз) нұсқа
// қажет: `g` бар regex `.test()` шақырғанда `lastIndex`-ті есте сақтайды да,
// кезекті шақыруда жалған `false` қайтарады.
const URL_SPLIT_RE = /(https?:\/\/[^\s]+)/g;
const URL_TEST_RE = /^https?:\/\/\S+$/;

const looksLikeImage = (url) => !!url && IMAGE_EXT.test(url);
const looksLikeAudio = (url) => !!url && AUDIO_EXT.test(url);

/** Хабарлама бір ғана сілтемеден тұра ма (мобильде медиа көбіне солай келеді). */
const soleUrl = (textValue) => {
  const v = (textValue || "").trim();
  return /^https?:\/\/\S+$/.test(v) ? v : null;
};

/** Мәтін ішіндегі сілтемелерді басуға болатын етеді.
 *  Бұрын бүкіл мәтін жай ғана `<div>{msg.message}</div>` болатын, сондықтан
 *  әр сілтеме басылмайтын жазу күйінде тұратын. */
const linkify = (textValue) =>
  String(textValue)
    .split(URL_SPLIT_RE)
    .map((part, i) =>
      URL_TEST_RE.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline break-all hover:opacity-80"
        >
          {part}
        </a>
      ) : (
        <span key={i}>{renderBold(part, `${i}-`)}</span>
      ),
    );

/** Оқылғаны: ✓ — жіберілді, ✓✓ (көк) — клиент оқыды (Telegram/WhatsApp-тағыдай).
 *  Бұрын бір ғана белгі тұрып, мөлдірлігі ғана өзгеретін — оқылғаны білінбейтін. */
const ReadTicks = ({ read, title }) => (
  <svg
    viewBox="0 0 20 12"
    className={`w-4 h-3 shrink-0 ${read ? "text-sky-300" : "text-white/60"}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-label={title}
  >
    <title>{title}</title>
    <path d="M1 6.5 4.5 10 11 2" />
    {read && <path d="M8.5 9.2 9.3 10 15.8 2" />}
  </svg>
);

const MessageBubble = ({ msg, isOwn, onReply, onDelete, onEdit }) => {
  const t = useT();
  const isAudio = msg.message_type === "audio";
  const isLocation = msg.message_type === "location";
  // Дауыс `audio_url`-де де, `file_url`-де де келуі мүмкін (мобильде — әрқашан
  // file_url). Бұрын тек `audio_url` оқылатын, сондықтан аудио МҮЛДЕ көрінбейтін.
  // Тіркеме `file_url`-де болмай, хабарламаның МӘТІНІ болып келуі де мүмкін —
  // скриншотта дауыстық хабарлама дәл солай жай сілтеме болып тұрған.
  const inlineUrl = soleUrl(msg.message);
  const audioSrc = isAudio
    ? msg.audio_url || msg.file_url || inlineUrl
    : looksLikeAudio(msg.file_url)
      ? msg.file_url
      : looksLikeAudio(inlineUrl)
        ? inlineUrl
        : null;
  const imageSrc =
    !audioSrc && looksLikeImage(msg.file_url)
      ? msg.file_url
      : !audioSrc && looksLikeImage(inlineUrl)
        ? inlineUrl
        : null;
  // Қалған кез келген тіркеме — жай сілтеме.
  const fileSrc = !audioSrc && !imageSrc && msg.file_url ? msg.file_url : null;
  const ann = msg.announcement;
  // Мобильді клиенттер тіркеменің URL-ін `message` өрісіне де қайталап жазады —
  // тіркеменің астында сол сілтеме мәтін болып тұрмауы керек.
  const attachmentUrl = audioSrc || imageSrc || fileSrc;
  const call = parseVoiceCall(msg.message);
  const showText = !call && msg.message && (msg.message || "").trim() !== attachmentUrl;
  return (
    // Өз хабарымда әрекет батырмалары СОЛ жақта — көпіршік оң шетке тегіс тиеді.
    <div className={`flex items-start gap-1 mb-1.5 group ${isOwn ? "flex-row-reverse" : ""}`}>
      <div
        className={`min-w-0 max-w-[85%] sm:max-w-[75%] lg:max-w-[62%] rounded-lg px-3 py-1.5 text-left ${
          isOwn
            ? "bg-[#5B4BE0] text-white rounded-tr-sm"
            : "bg-bg-card border border-border-primary text-white rounded-tl-sm"
        }`}
      >
        {msg.reply_to_id && (
          <div className="text-xs opacity-70 border-l-2 pl-2 mb-1 truncate">
            ↩ #{msg.reply_to_id}
          </div>
        )}
        {/* Жарнама карточкасы. Мұндай хабарламада мәтін БОС болады — бүкіл
            мазмұн осында, сондықтан бұрын бос көпіршік көрінетін. */}
        {ann && (
          <a
            href={`https://agroland.kz/announcement/${ann.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex gap-2 items-center mb-1 rounded-lg overflow-hidden bg-black/20 p-2 hover:bg-black/30 transition-colors"
          >
            {ann.main_image_url && (
              <img
                src={ann.main_image_url}
                alt=""
                className="w-12 h-12 object-cover rounded"
                loading="lazy"
              />
            )}
            <span className="min-w-0">
              <span className="block text-sm truncate">{ann.title}</span>
              {ann.price != null && (
                <span className="block text-xs opacity-70">{ann.price} ₸</span>
              )}
            </span>
          </a>
        )}
        {imageSrc && (
          <a href={imageSrc} target="_blank" rel="noopener noreferrer" className="block mb-1">
            <img
              src={imageSrc}
              alt={msg.file_name || ""}
              className="rounded-lg max-h-64 w-auto"
              loading="lazy"
            />
          </a>
        )}
        {fileSrc && (
          <a
            href={fileSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm underline mb-1"
          >
            📎 {msg.file_name || t("chat.file")}
          </a>
        )}
        {audioSrc && (
          <audio controls src={audioSrc} className="mb-1 max-w-full" />
        )}
        {isLocation && msg.latitude && (
          <a
            href={`https://maps.google.com/?q=${msg.latitude},${msg.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm underline"
          >
            📍 {msg.location_name || t("chat.location")}
          </a>
        )}
        {call && (
          <div className="flex items-center gap-2 text-sm">
            <PhoneIcon className={`w-4 h-4 ${call.status === "completed" ? "" : "text-red-300"}`} />
            <span>{voiceCallLabel(call, t)}</span>
          </div>
        )}
        {showText && (
          <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm leading-relaxed">
            {linkify(msg.message)}
          </div>
        )}
        <div className="flex items-center justify-end gap-1.5 mt-0.5">
          <span className="text-[11px] opacity-60">{formatTime(msg.created_at)}</span>
          {/* Клиент «Қолдау» дегенді көреді, ал панельде жауапты НАҚТЫ кім
              жазғаны тұрады — бұрын оны білудің мүлде жолы жоқ еді. */}
          {msg.sent_by_name && (
            <span className="text-[11px] opacity-60 truncate">· {msg.sent_by_name}</span>
          )}
          {isOwn && <ReadTicks read={!!msg.is_read} title={t(msg.is_read ? "chat.read" : "chat.sent")} />}
        </div>
      </div>
      <div className="opacity-0 group-hover:opacity-100 flex flex-col gap-1 pt-1 text-text-secondary">
        <button onClick={() => onReply(msg)} title={t("chat.reply")}>
          <ArrowUturnLeftIcon className="w-4 h-4" />
        </button>
        {isOwn && (
          <>
            <button onClick={() => onEdit(msg)} title={t("chat.editMessage")}>
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            <button onClick={() => onDelete(msg)} title={t("chat.delete")}>
              <TrashIcon className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const CloseDialog = ({ onClose, onConfirm }) => {
  const t = useT();
  const [note, setNote] = useState("");
  const [tag, setTag] = useState("");
  const [tags, setTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const addTag = () => {
    const t = tag.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTag("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-card border border-border-primary rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold text-white mb-4">{t("chat.close")}</h3>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("chat.resolutionNote")}
          rows={3}
          className="w-full p-3 rounded-lg bg-bg-primary border border-gray-700 text-white mb-3"
        />
        <div className="flex gap-2 mb-3">
          <input
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
            placeholder={t("chat.addTag")}
            className="flex-1 px-3 py-2 rounded-lg bg-bg-primary border border-gray-700 text-white"
          />
          <button
            onClick={addTag}
            className="px-3 py-2 rounded-lg border border-gray-700 text-white"
          >
            +
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {tags.map((t) => (
            <span
              key={t}
              className="px-2 py-1 rounded-full bg-accent-grad text-white text-xs cursor-pointer"
              onClick={() => setTags(tags.filter((x) => x !== t))}
            >
              {t} ×
            </span>
          ))}
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-700 text-text-secondary"
          >
            {t("app.cancel")}
          </button>
          <button
            onClick={async () => {
              setSubmitting(true);
              try {
                await onConfirm(note, tags);
              } finally {
                setSubmitting(false);
              }
            }}
            className="px-4 py-2 rounded-lg bg-accent-grad text-white"
          >
            {submitting ? "..." : t("app.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Анықталмаған мәндерді алып тастайды — біріктіргенде олар бар өрісті
 *  өшіріп кетпеуі үшін. */
const defined = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/** Екі тізімді id бойынша біріктіріп, уақыты бойынша реттейді.
 *
 *  АЛМАСТЫРМАЙДЫ, ҮСТІНЕ ҚОСАДЫ: сокеттен келетін нұсқада REST жүктеген
 *  кейбір өріс болмайды (мыс. `sent_by_name` — жауапты кім жазғаны), сондықтан
 *  жай ғана `set` етсек ол өрістер жоғалып кетер еді. */
const mergeMessages = (prev, incoming) => {
  if (!incoming || incoming.length === 0) return prev;
  const byId = new Map();
  [...prev, ...incoming].forEach((m) => {
    if (!m || m.id === undefined || m.id === null) return;
    const key = String(m.id);
    byId.set(key, { ...(byId.get(key) || {}), ...defined(m) });
  });
  return [...byId.values()].sort(
    (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
  );
};

const Conversation = ({ chatId, operatorUser, onChatChanged, onUserProfile }) => {
  const t = useT();
  const { language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [showClose, setShowClose] = useState(false);
  const [profile, setProfile] = useState(null);
  const [roomName, setRoomName] = useState(null);
  const [actorUserId, setActorUserId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const scrollRef = useRef(null);
  const socketRef = useRef(null);
  const recorderRef = useRef(null);
  const fileInputRef = useRef(null);
  const call = useVoiceCall(socketRef);

  // load history
  const fetchMessages = useCallback(async () => {
    if (!chatId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getMessages(chatId, { limit: 100 });
      setMessages(data.items || []);
      if (data.actor_user_id) setActorUserId(data.actor_user_id);
      if (data.room_name) setRoomName(data.room_name);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [chatId]);

  // load user profile (for socket join)
  const fetchProfile = useCallback(async () => {
    if (!chatId) return;
    try {
      const data = await getUserProfile(chatId);
      setProfile(data);
    } catch (e) {
      console.warn("profile fetch failed", e);
    }
  }, [chatId]);

  useEffect(() => {
    fetchMessages();
    fetchProfile();
  }, [fetchMessages, fetchProfile]);

  // socket: join room
  useEffect(() => {
    if (!chatId) return;
    const socket = initializeSocket();
    socketRef.current = socket;
    if (!socket) return;

    const joinSuccess = (data) => {
      if (data?.room_name) setRoomName(data.room_name);
      if (Array.isArray(data?.messages)) {
        const normalized = data.messages.map((m) => ({
          id: m.id,
          chat_id: chatId,
          sender_id: m.senderId ?? m.sender_id,
          receiver_id: m.receiverId ?? m.receiver_id,
          message: m.message,
          message_type: m.messageType || m.message_type || "text",
          is_read: m.isRead ?? m.is_read,
          created_at: m.timestamp || m.created_at,
          file_url: m.fileUrl || m.file_url,
          file_name: m.fileName || m.file_name,
          audio_url: m.audioUrl || m.audio_url,
          reply_to_id: m.replyToId || m.reply_to_id,
          sent_by_user_id: m.sent_by_user_id,
          announcement: m.announcement,
          latitude: m.latitude,
          longitude: m.longitude,
          location_name: m.locationName || m.location_name,
        }));
        // REST-тен жүктелген тарихты АЛМАСТЫРМАЙМЫЗ, БІРІКТІРЕМІЗ.
        //
        // Кезектегі (әлі қабылданбаған) чатта оператор әңгіменің қатысушысы
        // емес, сондықтан сокет бос тізім қайтарады. Бұрын сол бос тізім
        // тарихтың үстіне жазылып, хабарламалар бір көрініп, сосын
        // жоғалып кететін.
        setMessages((prev) => mergeMessages(prev, normalized));
      }
    };
    const newMessage = (data) => {
      setMessages((prev) => [
        ...prev,
        {
          id: data.id || Date.now(),
          chat_id: chatId,
          sender_id: data.senderId ?? data.sender_id,
          receiver_id: data.receiverId ?? data.receiver_id,
          message: data.message,
          message_type: data.messageType || data.message_type || "text",
          created_at: data.timestamp || new Date().toISOString(),
          is_read: false,
          file_url: data.fileUrl || data.file_url,
          file_name: data.fileName || data.file_name,
          audio_url: data.audioUrl || data.audio_url,
          reply_to_id: data.replyToId || data.reply_to_id,
          announcement: data.announcement,
          latitude: data.latitude,
          longitude: data.longitude,
          location_name: data.locationName || data.location_name,
          sent_by_user_id: data.sent_by_user_id,
          // Өз жауабымыздың астында атымыз бірден тұрсын (қайта жүктемей).
          sent_by_name:
            data.sent_by_user_id && data.sent_by_user_id === operatorUser?.id
              ? operatorUser?.name
              : undefined,
        },
      ]);
    };
    // Клиент оқыды → өз хабарларымыз ✓✓ болады (бетті жаңартпай).
    const messageRead = (data) => {
      if (Number(data?.room_id) !== Number(chatId)) return;
      const upTo = Number(data.last_read_message_id || data.message_id);
      if (!upTo) return;
      setMessages((prev) =>
        prev.map((x) => (x.id <= upTo && x.sender_id !== data.reader_id && !x.is_read ? { ...x, is_read: true } : x)),
      );
    };
    socket.on("join_chat_success", joinSuccess);
    socket.on("chat_message", newMessage);
    socket.on("message_read", messageRead);

    if (!socket.connected) socket.connect();

    const emitJoin = () => {
      // `other_user_id` ЕМЕС, `room_id`.
      //
      // Бұрын панель өз аккаунтының токенімен `other_user_id` арқылы қосылатын,
      // сондықтан бекенд ОПЕРАТОР ↔ КЛИЕНТ деген МҮЛДЕМ БАСҚА чат ашатын:
      // клиент жауапты «Қолдау» емес, бейтаныс аккаунттан көретін, ал кезектегі
      // нақты чат жауапсыз қалатын. Чат id-імен қосылғанда оператор қолдау
      // чатының өз бөлмесіне кіреді.
      socket.emit("join_chat", { room_id: chatId });
    };
    if (socket.connected) emitJoin();
    else socket.on("connect", emitJoin);

    return () => {
      socket.off("join_chat_success", joinSuccess);
      socket.off("chat_message", newMessage);
      socket.off("message_read", messageRead);
      socket.off("connect", emitJoin);
      // `initializeSocket()` әр шақырғанда ЖАҢА қосылым жасайды
      // (`forceNew: true`), сондықтан ескісін жауып кетпесек, чат
      // ауыстырған сайын қосылымдар жиналып қалады.
      socket.disconnect();
    };
  }, [chatId]);

  // autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  /** Бір ғана шығу нүктесі — мәтін де, тіркеме де осы арқылы кетеді. */
  const emitMessage = (payload) => {
    if (!roomName || !socketRef.current) return false;
    socketRef.current.emit("chat_message", {
      room_name: roomName,
      reply_to_id: replyTo?.id,
      ...payload,
    });
    setReplyTo(null);
    return true;
  };

  const sendMessage = () => {
    const body = text.trim();
    if (!body) return;
    if (emitMessage({ message: body, message_type: "text" })) setText("");
  };

  /** Файл/сурет: алдымен GCS-ке жүктеп, сосын сілтемесін жібереміз
   *  (чат сокеті екілік дерек қабылдамайды). */
  const sendFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadChatFile(file);
      emitMessage({ message: "", message_type: "file", file_url: url });
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  /** Дауыстық хабарлама. Бекенд `{audio_url}` қайтарады, бірақ чат оны
   *  `file_url` бағанында сақтайды — мобильді клиенттер де солай жібереді,
   *  сондықтан пішінді солармен бірдей ұстаймыз. */
  const sendAudio = async (blob) => {
    setUploading(true);
    try {
      const file = new File([blob], `voice_${Date.now()}.webm`, {
        type: blob.type || "audio/webm",
      });
      const url = await uploadChatAudio(file);
      emitMessage({ message: "", message_type: "audio", file_url: url });
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        if (chunks.length) sendAudio(new Blob(chunks, { type: rec.mimeType }));
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const stopRecording = (send = true) => {
    const rec = recorderRef.current;
    if (!rec) return;
    if (!send) rec.ondataavailable = null;
    rec.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  const handleClose = async (note, tags) => {
    try {
      await closeChat(chatId, note || null, tags || []);
      setShowClose(false);
      onChatChanged?.();
    } catch (e) {
      alert(`Close failed: ${e.message}`);
    }
  };

  if (!chatId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-text-secondary">
        <UserCircleIcon className="w-20 h-20 opacity-30 mb-3" />
        <p>{t("chat.selectChat")}</p>
      </div>
    );
  }

  return (
    // text-left: App.css-тегі CRA-ның `text-align: center`-і көпіршік мәтінін ортаға тартатын.
    <div className="flex flex-col h-full text-left">
      {/* Header — анимациясыз, дөңгелектеусіз жолақ (бұрын .dark-card hover-де «секіретін») */}
      <header className="bg-bg-card border-b border-border-primary px-4 py-2.5 flex items-center justify-between gap-3">
        <button
          onClick={() => onUserProfile?.(profile)}
          className="flex items-center gap-3 hover:opacity-80"
        >
          <div className="w-10 h-10 rounded-full bg-accent-grad flex items-center justify-center text-white font-bold">
            {profile?.user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="text-left">
            <div className="text-white font-semibold">{profile?.user?.name || "..."}</div>
            <div className="text-xs text-text-secondary">{profile?.user?.phone_number}</div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => call.startCall(profile?.user?.id)}
            disabled={!profile?.user?.id || call.status !== "idle"}
            title={t("call.start")}
            className="p-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 disabled:opacity-40"
          >
            <PhoneIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowClose(true)}
            className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20"
          >
            {t("chat.close")}
          </button>
        </div>
      </header>

      {/* Қоңырау жолағы. Аудио элементі ӘРҚАШАН DOM-да тұруы керек —
          шартты түрде сызсақ, ontrack келгенде srcObject қоятын жер болмай,
          дыбыс естілмей қалады. */}
      <audio ref={call.audioRef} autoPlay className="hidden" />
      {call.status !== "idle" && (
        <div className="flex items-center gap-3 px-4 py-2 bg-green-500/10 border-b border-green-500/30">
          <span className="inline-flex rounded-full h-2 w-2 bg-green-500" />
          <span className="text-sm text-green-300">
            {call.status === "ringing" && t("call.ringing")}
            {call.status === "connecting" && t("call.connecting")}
            {call.status === "active" &&
              `${t("call.active")} · ${String(Math.floor(call.seconds / 60)).padStart(2, "0")}:${String(
                call.seconds % 60,
              ).padStart(2, "0")}`}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={call.toggleMute}
              title={t("call.mute")}
              className={`p-2 rounded-lg border ${
                call.muted
                  ? "bg-yellow-500/20 border-yellow-500/40 text-yellow-300"
                  : "bg-bg-card border-border-primary text-text-secondary hover:text-white"
              }`}
            >
              <SpeakerXMarkIcon className="w-4 h-4" />
            </button>
            <button
              onClick={call.hangup}
              title={t("call.hangup")}
              className="p-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30"
            >
              <PhoneXMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {call.error && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border-b border-red-500/30 text-red-400 text-sm">
          <span>{call.error}</span>
          <button onClick={call.clearError} className="ml-auto">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 bg-bg-base">
        {/* Қате болса да, бұрын жүктелген хабарламаларды ЖАСЫРМАЙМЫЗ —
            қате шағын жолақ болып үстінде тұрады. Бұрын қате тарихты
            толық алмастырып, әңгіме бос болып көрінетін. */}
        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}
        {loading && messages.length === 0 ? (
          <div className="text-center text-text-secondary mt-10">{t("app.loading")}</div>
        ) : (
          messages.map((m, i) => (
            <React.Fragment key={m.id}>
            {/* Күн ауысқанда бөлгіш — бұрын тек сағат тұратын, қай күн екені белгісіз еді */}
            {m.created_at && (i === 0 || dayKey(messages[i - 1].created_at) !== dayKey(m.created_at)) && (
              <div className="flex justify-center my-3">
                <span className="px-2.5 py-0.5 rounded-md bg-bg-card border border-border-primary text-[11px] text-text-secondary">
                  {formatDay(m.created_at, t, language)}
                </span>
              </div>
            )}
            <MessageBubble
              msg={m}
              isOwn={m.sender_id === operatorUser?.id || m.sender_id === actorUserId}
              onReply={setReplyTo}
              onDelete={(msg) => {
                socketRef.current?.emit("delete_message", { message_id: msg.id });
                setMessages((prev) => prev.filter((x) => x.id !== msg.id));
              }}
              onEdit={(msg) => {
                const newText = window.prompt("Edit message", msg.message);
                if (newText && newText !== msg.message) {
                  socketRef.current?.emit("edit_message", {
                    message_id: msg.id,
                    message: newText,
                  });
                  setMessages((prev) =>
                    prev.map((x) =>
                      x.id === msg.id ? { ...x, message: newText } : x
                    )
                  );
                }
              }}
            />
            </React.Fragment>
          ))
        )}
      </div>

      {/* Input */}
      <div className="bg-bg-card border-t border-border-primary p-2.5">
        {replyTo && (
          <div className="flex items-center justify-between bg-bg-primary p-2 rounded-lg mb-2 text-sm text-text-secondary">
            <span className="truncate">↩ {replyTo.message?.slice(0, 80)}</span>
            <button onClick={() => setReplyTo(null)}>
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              sendFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || recording}
            title={t("chat.attach")}
            className="p-2.5 rounded-md text-text-secondary hover:text-white hover:bg-bg-primary disabled:opacity-40"
          >
            <PaperClipIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => (recording ? stopRecording(true) : startRecording())}
            disabled={uploading}
            title={recording ? t("chat.stopRecording") : t("chat.recordVoice")}
            className={`p-2.5 rounded-md disabled:opacity-40 ${
              recording
                ? "bg-red-600 text-white"
                : "text-text-secondary hover:text-white hover:bg-bg-primary"
            }`}
          >
            {recording ? <StopIcon className="w-5 h-5" /> : <MicrophoneIcon className="w-5 h-5" />}
          </button>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={t("chat.messagePlaceholder")}
            rows={1}
            className="flex-1 min-w-0 px-3 py-2 rounded-md bg-bg-primary border border-border-primary text-white text-sm resize-none focus:outline-none focus:border-[#5B4BE0]"
          />
          <button
            onClick={sendMessage}
            disabled={!text.trim() || uploading || recording}
            className="p-2.5 rounded-md bg-[#5B4BE0] text-white hover:bg-[#4F40D6] disabled:opacity-40"
          >
            <PaperAirplaneIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {showClose && (
        <CloseDialog onClose={() => setShowClose(false)} onConfirm={handleClose} />
      )}
    </div>
  );
};

export default Conversation;
